import axios from "axios";
import flatten from "flat";

import { authConstants, logout } from "../actions/auth/index";

import { APP_PDC_URL } from "../../config";
import { displayErrorToast, displaySuccessToast } from "../actions/overflowNotification";
import { createHttpHeader, createHttpHeaderWithAuthorization } from "./http-header";

export async function performCall(
  url, token, method, urlParams, body, additionalHeaders = {}
) {
  let queryString = getQueryString(urlParams);

  queryString = queryString !== "" ? `?${queryString}` : "";

  const defaultHeaders = token ? createHttpHeaderWithAuthorization(token) : createHttpHeader();

  return await axios({
    url: `${url}${queryString}`,
    method: method,
    data: body && JSON.stringify(body),
    headers: {
      ...defaultHeaders,
      ...additionalHeaders
    }
  })
    .then((response) => {
      return { ...response, ok: true };
    })
    .catch((error) => {
      return { ...error.response, ok: false };
    });
}

export async function restApiCall(
  url,
  auth,
  method,
  urlParams,
  body,
  dispatch,
  refreshToken,
  displayError = false,
  displaySuccess = false,
  additionalHeaders = {},
  successText = null
) {
  let response = await performCall(
    url, auth && auth.securityToken, method, urlParams, body, additionalHeaders
  );
  if (!successText) {
    successText = "Operazione correttamente presa in carico";
  }
  if (!response.ok) {
    if (response.status === 401 && auth) {
      const tokenResponse = await performCall(
        `${APP_PDC_URL}/auth/login/agyo`, null, "POST", null, {
          id: auth.id,
          digest: refreshToken
        }
      );

      if (!tokenResponse.ok) {
        logout()(dispatch);
        return undefined;
      }

      const token = tokenResponse.data.token;

      dispatch({
        type: authConstants.LOGIN_SUCCESS,
        payload: {
          loginAuth: {
            securityToken: token,
            id: auth.id,
            appName: auth.appName,
            refreshToken: refreshToken
          }
        }
      });

      response = await performCall(
        url, token, method, urlParams, body
      );

      if (!response.ok) {
        if (response.status === 401) {
          logout()(dispatch);
        } else {
          const text = response.data.message;
          if (displayError) {
            displayErrorToast(text)(dispatch);
          }

          const errResponse = {
            status: response.status,
            ...response.data
          };
          throw errResponse;
        }
      }
    } else if (response.status === 403 && response.data.code === "403.1") {
      dispatch({
        type: authConstants.LOGIN_ERROR,
        error: response.data,
        errorInfo: response.data
      });
    } else {
      const text = response.data.message || response.statusText;
      if (displayError) {
        displayErrorToast(text)(dispatch);
      }

      const errResponse = { status: response.status, ...response.data };
      throw errResponse;
    }
  }

  if (displaySuccess) {
    displaySuccessToast(successText)(dispatch);
  }

  return response.data;
}

export function getRestCall(
  url, auth, urlParams, dispatch, refreshToken, displayError = false, displaySuccess = false, additionalHeaders = {}
) {
  const token = auth && auth.securityToken;
  const defaultHeaders = token ? createHttpHeaderWithAuthorization(token) : createHttpHeader();

  return restApiCall(
    url, auth, "GET", urlParams, null, dispatch, refreshToken, displayError, displaySuccess, Object.assign(defaultHeaders, additionalHeaders)
  );
}

export function postRestCall(
  url,
  auth,
  urlParams,
  body,
  dispatch,
  refreshToken,
  displayError = false,
  displaySuccess = false,
  additionalHeaders = {},
  successText = null
) {
  const token = auth && auth.securityToken;
  const defaultHeaders = token ? createHttpHeaderWithAuthorization(token) : createHttpHeader();

  return restApiCall(
    url,
    auth,
    "POST",
    urlParams,
    body,
    dispatch,
    refreshToken,
    displayError,
    displaySuccess,
    Object.assign(defaultHeaders, additionalHeaders),
    successText
  );
}

export function deleteRestCall(
  url, auth, urlParams, body, dispatch, refreshToken, displayError = false, displaySuccess = false, additionalHeaders = {}
) {
  const token = auth && auth.securityToken;
  const defaultHeaders = token ? createHttpHeaderWithAuthorization(token) : createHttpHeader();
  return restApiCall(
    url,
    auth,
    "DELETE",
    urlParams,
    body,
    dispatch,
    refreshToken,
    displayError,
    displaySuccess,
    Object.assign(defaultHeaders, additionalHeaders)
  );
}

export function patchRestCall(
  url, auth, urlParams, body, dispatch, refreshToken, displayError = true, additionalHeaders = {}
) {
  const token = auth && auth.securityToken;
  const defaultHeaders = token ? createHttpHeaderWithAuthorization(token) : createHttpHeader();
  return restApiCall(
    url, auth, "PATCH", urlParams, body, dispatch, refreshToken, displayError, false, Object.assign(defaultHeaders, additionalHeaders)
  );
}

export function putRestCall(
  url, auth, urlParams, body, dispatch, refreshToken, displayError = true, additionalHeaders = {}
) {
  const token = auth && auth.securityToken;
  const defaultHeaders = token ? createHttpHeaderWithAuthorization(token) : createHttpHeader();
  return restApiCall(
    url, auth, "PUT", urlParams, body, dispatch, refreshToken, displayError, false, Object.assign(defaultHeaders, additionalHeaders)
  );
}

/**
 * Trasform a nested object in a query string
 * @param params nested object
 */
export function getQueryString(params) {
  let stringParams = "";
  if (params) {
    // cancello chiavi null
    Object.keys(params).forEach((key) => params[key] == null && delete params[key]);

    /* andiamo a costruire la stringa degli elementi array es param.miaProprietà = ["valore1", valore2]
     *  al termine la stringa dovrà essere &miaProprietà=valore1&miaProprietà=valore2. Questo soprattutto
     *  per richiamare api di tipo filtro
     */
    for (let key in params) {
      if (typeof params[key] !== "string") {
        let keyParam = key;
        // scorro i valori di array e costruisco la stringa parametri
        for (let i = 0; i < params[key].length; i++) {
          let currentValueParams = params[key][i];
          stringParams += keyParam + "=" + currentValueParams + "&";
        }
      }
    }

    // elimino da params gli oggetti questi verranno concatenati a mano
    Object.keys(params).forEach((key) => {
      typeof params[key] === "object" && delete params[key];
    });
  }

  // creo la stringa dei parametri di tipo stringa
  stringParams += params
    ? Object.entries(flatten(params, { safe: true }))
      .map((key) => {
        return `${key[0]}=${encodeURIComponent(key[1] + "")}`;
      })
      .join("&")
    : "";

  return stringParams;
}
